import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { ClientProfile, ClientStatus, Me, Tag } from '../lib/api';
import { navigate } from '../lib/router';
import { fmtDate, titleCase } from '../lib/format';
import { Empty, Kpi, PageHead, Pill } from '../components/ui';
import { TagChips } from '../components/tags';
import { ScoreBadge } from '../components/matches';
import { ProfileForm } from '../components/ProfileForm';

export const STATUS_KIND: Record<ClientStatus, 'ok' | 'warn' | 'muted'> = { active: 'ok', prospect: 'warn', inactive: 'muted' };

export function Clients({ me }: { me: Me }) {
  const [items, setItems] = useState<ClientProfile[] | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    api<{ items: ClientProfile[] }>('/api/clients').then((r) => setItems(r.items)).catch((e: Error) => setErr(e.message));
  }, []);
  useEffect(() => { load(); api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([])); }, [load]);

  const canEdit = me.role !== 'viewer';
  const list = items ?? [];

  return (
    <>
      <PageHead
        title="Client Profiles"
        sub="One profile per client: capabilities, priorities and opportunity criteria, each scored against every open opportunity from live sources"
        actions={
          <>
            {list.length > 0 && (
              <label className="client-switch">
                <span className="muted sm">Open client</span>
                <select className="select" value="" onChange={(e) => { if (e.target.value) navigate('clients', e.target.value); }}>
                  <option value="">Select…</option>
                  {list.map((c) => <option key={c.id} value={c.id}>{c.name}{c.new_matches ? ` (${c.new_matches} new)` : ''}</option>)}
                </select>
              </label>
            )}
            {canEdit && <button className="primary-btn" onClick={() => setCreating(true)}>+ New profile</button>}
          </>
        }
      />
      {err && <div className="error-text">{err}</div>}
      <div className="kpi-row">
        <Kpi label="Profiles" value={items ? list.length : '…'} />
        <Kpi label="Active clients" value={list.filter((c) => c.status === 'active').length} />
        <Kpi label="New since review" value={list.reduce((s, c) => s + c.new_matches, 0)} />
        <Kpi label="Priority" value={list.reduce((s, c) => s + c.priority, 0)} hint="relevant + pursuing" />
        <Kpi label="Pursuing" value={list.reduce((s, c) => s + c.pursuing, 0)} />
        <Kpi label="Open alerts" value={list.reduce((s, c) => s + c.alerts_open, 0)} />
      </div>

      <div className="panel">
        <div className="panel-body table-wrap">
          {items === null ? <Empty>Loading…</Empty> : list.length === 0 ? (
            <Empty>No client profiles yet.{canEdit && ' Create the first one with “New profile”.'}</Empty>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Account</th><th>Sector</th><th>Technology focus</th><th>Top fit</th><th className="num">New</th><th className="num">Priority</th><th className="num">Pursuing</th><th className="num">Alerts</th><th>Status</th><th>Reviewed</th></tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => navigate('clients', c.id)}>
                    <td><strong>{c.name}</strong>{c.size && <div className="muted sm">{c.size}</div>}</td>
                    <td className="muted">{c.sector ?? '—'}</td>
                    <td>{c.technology_tags.length ? <TagChips tags={c.technology_tags} all={tags} max={3} /> : <span className="muted">—</span>}</td>
                    <td><ScoreBadge score={c.top_score} /></td>
                    <td className="num">{c.new_matches > 0 ? <span className="pos">{c.new_matches}</span> : 0}</td>
                    <td className="num">{c.priority}</td>
                    <td className="num">{c.pursuing}</td>
                    <td className="num">{c.alerts_open > 0 ? <span className="neg">{c.alerts_open}</span> : 0}</td>
                    <td><Pill kind={STATUS_KIND[c.status]}>{titleCase(c.status)}</Pill></td>
                    <td className="muted">{c.last_reviewed_at ? fmtDate(c.last_reviewed_at) : 'never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {creating && <ProfileForm tags={tags} onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); navigate('clients', id); }} />}
    </>
  );
}
